import type { FilterSchema } from '@/shared/components/filter/validation'

import React, { useMemo } from 'react'
import { StarIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useSavedFiltersQuery } from '@/entities/report'

import { Button } from '@/shared/components/ui/button'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import { SavedFilterType } from '@/shared/generated'
import { useModals } from '@/shared/lib/modal-manager'
import { cyrb53 } from '@/shared/tools/utils'

export function SavedReportIndicator({
	filters,
	type = SavedFilterType.Products,
	disabled,
}: {
	filters: FilterSchema[]
	type: SavedFilterType
	disabled?: boolean
}) {
	const { t } = useTranslation()

	const { showModal } = useModals()
	const savedFiltersQuery = useSavedFiltersQuery({
		select: (data) => data.filter((item) => item.type === type),
	})

	const exists = useMemo(() => {
		const curr = cyrb53(JSON.stringify(filters))

		return (savedFiltersQuery.data || []).find(
			(item) => cyrb53(JSON.stringify(item.filters)) === curr
		)
	}, [savedFiltersQuery.data, filters])

	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<Button
					disabled={disabled}
					variant='ghost'
					size='icon'
					onClick={() =>
						exists
							? showModal('UPDTAE_ASSORTMENT_REPORT', { report: exists })
							: showModal('CREATE_ASSORTMENT_REPORT', {
									filters,
									type,
								})
					}
				>
					{exists ? (
						<StarIcon className='fill-warning text-warning' />
					) : (
						<StarIcon />
					)}
				</Button>
			</TooltipTrigger>
			<TooltipContent>
				{disabled
					? t('app.save_report.disabled', {
							defaultValue: 'Please provide some filters to save report',
						})
					: t('app.save_report')}
			</TooltipContent>
		</Tooltip>
	)
}
